import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

const ConfirmContext = createContext(null);

/**
 * Promise-based replacement for window.confirm().
 *
 * The native dialog blocks the tab, can't be styled, isn't announced properly,
 * and — worst — Chrome lets a user tick "prevent additional dialogs", after
 * which confirm() silently returns false and the guarded action just stops
 * happening with no explanation.
 *
 * Deliberately shaped so call sites barely change:
 *
 *   if (!window.confirm('Delete this?')) return;
 *   if (!await confirm('Delete this?')) return;
 *
 * Accepts a string, or an options object for anything richer:
 *
 *   await confirm({
 *     title: 'Delete this brand?',
 *     message: 'Products using it keep their brand name.',
 *     confirmLabel: 'Delete',
 *     tone: 'danger',
 *   })
 *
 * Pass `reasons` to make the dialog collect one before it will confirm. It then
 * resolves to { reason, note } instead of true — still truthy, so the usual
 * `if (!await confirm(...)) return;` guard at the call site is unchanged:
 *
 *   const outcome = await confirm({
 *     title: 'Mark this quote as lost?',
 *     reasons: [{ value: 'Timeline', label: 'Timeline' }, ...],
 *     noteRequiredFor: 'Other',
 *   })
 *
 * `reasons` also accepts groups — [{ label, options: [{value,label}] }] — which
 * render as <optgroup>s.
 *   if (!outcome) return;
 *   post({ lostReason: outcome.reason, lostReasonNote: outcome.note });
 */
export const ConfirmProvider = ({ children }) => {
    const [request, setRequest] = useState(null);
    const resolverRef = useRef(null);

    const confirm = useCallback((options) => {
        const opts = typeof options === 'string' ? { message: options } : (options || {});
        return new Promise(resolve => {
            resolverRef.current = resolve;
            setRequest({
                title: opts.title || 'Are you sure?',
                message: opts.message || '',
                confirmLabel: opts.confirmLabel || 'Confirm',
                cancelLabel: opts.cancelLabel || 'Cancel',
                tone: opts.tone || 'default',
                reasons: opts.reasons || null,
                reasonLabel: opts.reasonLabel || '',
                notePlaceholder: opts.notePlaceholder || '',
                noteRequiredFor: opts.noteRequiredFor || '',
                requireDate: opts.requireDate || false,
                dateLabel: opts.dateLabel || '',
                minDate: opts.minDate || '',
            });
        });
    }, []);

    const settle = useCallback((result) => {
        setRequest(null);
        const resolve = resolverRef.current;
        resolverRef.current = null;
        // Resolve after clearing state so the caller never sees a half-torn-down
        // dialog if it immediately opens another one.
        if (resolve) resolve(result);
    }, []);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {request && (
                <ConfirmDialog
                    {...request}
                    onConfirm={(payload) => settle(payload ?? true)}
                    onCancel={() => settle(false)}
                />
            )}
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
    return ctx;
};
